import { Component, EventEmitter, Inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MdsIcon, MdsIconsModule } from '@mds/icons';
import { ComplianceData, ComplianceExemption, GridOverrides, RowSelection } from '@mds/common';
import { ButtonComponent, GridModule } from '@mds/ui';
import { Subject } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { CommonModule } from '@angular/common';
import { ColDef, GridApi } from 'ag-grid-community';
import { ComplianceQuestionnaireCommentsComponent } from '../compliance-questionnaire-comments/compliance-questionnaire-comments.component';

enum StatementStatus {
  COMPLIANT = 'compliant',
  NON_COMPLIANT = 'non-compliant',
  COMPLIANT_WITH_EXEMPTION = 'compliant-with-exemption'
}

@Component({
  selector: 'app-compliance-questionnaire-status',
  templateUrl: './compliance-questionnaire-status.component.html',
  styleUrls: ['./compliance-questionnaire-status.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MdsIconsModule,
    MatIconModule,
    MatDialogModule,
    ButtonComponent,
    MatTooltipModule,
    GridModule,
    MatFormFieldModule,
    ComplianceQuestionnaireCommentsComponent
  ]
})
export class ComplianceQuestionnaireStatusComponent implements OnInit, OnDestroy {
  mdsIcon = MdsIcon;
  attachments = [];
  viewedAttachments = new Set<string>();
  submitDataEmitter = new EventEmitter();
  private destroy$ = new Subject<void>();
  comment = '';
  @ViewChild('confirmationActions')
  confirmationActions: TemplateRef<HTMLAllCollection>;
  commentSubmitted = false;
  currentStatement: StatementStatus;
  exemptionOverrides: GridOverrides;
  gridApi: GridApi;
  statementStatus = StatementStatus;
  constructor(
    private dialogRef: MatDialogRef<ComplianceQuestionnaireStatusComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ComplianceData
  ) {}

  ngOnInit(): void {
    this.createExemptionOverrides();
    this.currentStatement = this.data.declaration.status
      ? StatementStatus.COMPLIANT_WITH_EXEMPTION
      : StatementStatus.NON_COMPLIANT;
  }

  onCancel() {
    this.dialogRef.close();
  }

  onAttachmentDownload(index: number) {
    const { link } = this.data.attachments[index];
    if (!link) return;

    const downloadElement = document.createElement('a');
    downloadElement.setAttribute('target', '_blank');
    downloadElement.setAttribute('href', link);
    document.body.appendChild(downloadElement);
    downloadElement.click();
    downloadElement.remove();
  }

  onAttachmentOpenInNewTab(index: number) {
    const attachment = this.data.attachments[index];
    const url = attachment.preview_link || attachment.link;
    if (!url) return;

    window.open(url, '_blank', 'noopener,noreferrer');
    this.viewedAttachments.add(attachment.rid);
  }

  onStatusUpdate(status: string) {
    const payload = {
      status,
      skus: [this.data.selectedProducts[0].rid],
      comment: this.comment,
      commentSubmitted: this.commentSubmitted
    };
    this.submitDataEmitter.emit(payload);
  }

  changeComment(comment: string) {
    this.comment = comment;
    if (comment && this.commentSubmitted) {
      this.commentSubmitted = false;
    }
  }

  submitComment(isSubmitted: boolean) {
    this.commentSubmitted = isSubmitted;
  }

  private createExemptionOverrides() {
    // BE sends different payloads for updated and new data. refactor when fixed.
    const rowData = this.data?.sku_exemptions.length
      ? this.data?.sku_exemptions.map((exemption: ComplianceExemption) => ({
          actions: { delete: this.data.editable }, // Include actions like delete
          substance: exemption.substance,
          substance_rid: exemption.substance,
          substance_name: exemption.substance_name ?? '',
          child_substance: exemption.child_substance,
          secondary_substance_name: exemption.child_substance_name,
          reason: exemption.reason,
          notes: exemption.notes,
          max_proportion: exemption.max_proportion ?? '',
          scip_material_category: exemption.scip_material_category,
          scip_material_category_name: exemption.scip_material_category_name,
          scip_mixture_category: exemption.scip_mixture_category,
          scip_mixture_category_name: exemption.scip_mixture_category_name
        }))
      : [];

    this.exemptionOverrides = {
      columnDefs: this.getExemptionColumns(),
      rowData,
      defaultColDef: {
        flex: 1,
        minWidth: 100,
        enableValue: true,
        enablePivot: true,
        sortable: true,
        filter: true,
        editable: false,
        resizable: true
      },
      groupDefaultExpanded: 1,
      headerHeight: 56,
      groupSelectsChildren: false,
      suppressRowClickSelection: false,
      suppressAggFuncInHeader: false,
      rowSelection: RowSelection.SINGLE,
      getRowHeight: () => 48,
      pagination: false,
      paginationAutoPageSize: true
    };
  }

  private getExemptionColumns() {
    const columns: ColDef[] = [
      {
        headerName: 'Substance / Group',
        field: 'substance_name',
        tooltipField: 'substance_name',
        headerTooltip: 'Substance / Group',
        minWidth: 200
      },
      {
        headerName: 'Secondary Substance / Group',
        field: 'secondary_substance_name',
        tooltipField: 'secondary_substance_name',
        headerTooltip: 'Secondary Substance / Group',
        minWidth: 220
      },
      {
        headerName: 'Reason',
        field: 'reason',
        tooltipField: 'reason',
        headerTooltip: 'Reason'
      },
      {
        headerName: 'Concentration',
        field: 'max_proportion',
        tooltipField: 'max_proportion',
        headerTooltip: 'Concentration',
        minWidth: 130
      },
      {
        headerName: 'Notes',
        field: 'notes',
        tooltipField: 'notes',
        headerTooltip: 'Notes'
      },
      {
        headerName: 'SCIP Material Category',
        field: 'scip_material_category_name',
        tooltipField: 'scip_material_category_name',
        headerTooltip: 'SCIP Material Category',
        minWidth: 200
      },
      {
        headerName: 'SCIP Mixture Category',
        field: 'scip_mixture_category_name',
        tooltipField: 'scip_mixture_category_name',
        headerTooltip: 'SCIP Mixture Category',
        minWidth: 200
      }
    ];
    return columns;
  }

  onGridReady(gridApi: GridApi) {
    this.gridApi = gridApi;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
